import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { createSelector, select, Store } from '@ngrx/store';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { getActiveExperienceCode, OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import { ConfigurableFormComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/containers/configurable-form/configurable-form.component';
import { OnFieldChanged } from '@ordersnproducts/modules/shared/modules/configurable-form/models/on-field-changed';
import {
  AddedValuesResetActionOP,
  AddedValuesResetActionOPByFormID,
  LoadAddedValuesNotFormulatingActionOP,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/actions/open-platform/added-values.actions';
import { generateAddedValueFormName } from '@ordersnproducts/modules/order-builder/modules/rms-specification/helpers/form-added-value-name-generator';
import { AddedValueFieldConfig } from '@ordersnproducts/modules/order-builder/modules/rms-specification/models/form-added-value-field-config';
import {
  getAvailableOpenPlatformAddedValues,
  getOpenPlatformProductAddedValues,
  getTemperatureVaps,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/added-value.selectors';
import {
  getRmsRecipeSpecifications,
  getRmsRehydrate,
  getRmsUpdatespecifications,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/index';
import { RmsSpecificationRemoveRehydrateItem } from '@ordersnproducts/modules/order-builder/modules/rms-specification/actions/rms-specification.actions';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { getOpenPlatformContracts, getOpenPlatformContractsBalance } from '../../../../selectors/contracts.selectors';
import { ORDER_EXPERIENCES } from '@ordersnproducts/modules/shared/constants/order-experiences';
import { ExperiencesService, TemperatureFeatures } from '../../../../../../../shared/services/experiences.service';
import { combineLatest, Observable, of } from 'rxjs';
import { ValueAddedOP } from '@cemex-core/cmx-api';

const range = (n) => Array.from({ length: n }, (value, key) => key);

const balanceAndRehydrate = createSelector(
  getOpenPlatformContractsBalance,
  getRmsRehydrate,
  getActiveExperienceCode,
  getOpenPlatformContracts,
  getRmsRecipeSpecifications,
  getAvailableOpenPlatformAddedValues,
  getRmsUpdatespecifications,
  (contractsBalance, rehydrate, experienceCode, contracts, recipe, addedValues, updateForm) => {
    return {
      contractsBalance,
      rehydrate,
      experienceCode,
      contracts,
      recipe,
      addedValues,
      updateForm,
    };
  },
);

const isRehydratation = (
  id: number,
  rehydrateState: {
    [id: number]: {
      [key: string]: any;
    };
  },
  config: AddedValueFieldConfig,
  fieldName = config.name,
) => {
  if (!!rehydrateState && rehydrateState[id] && config.rehydrate) {
    const rehydratationValue = _safe(() => rehydrateState[id][fieldName]);
    return !!rehydratationValue || fieldName in rehydrateState[id];
  } else {
    return false;
  }
};

/**
 * Loads the product added values
 * This only applies the first time added values loads (To the first value added dropdown)
 */
@Injectable()
export class LoadProductAddedValuesNotFormulatingOpenPlatformCommandService implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}
  private preValueProduct = {};
  onFieldChanged(
    value: string,
    formGroup: UntypedFormGroup,
    form: ConfigurableFormComponent,
    config: AddedValueFieldConfig,
    prev: string,
  ): void {
    const productCode = _safe(() => formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value);
    if (prev === value && this.preValueProduct[form.data.id] === productCode) {
      return;
    }
    this.preValueProduct[form.data.id] = productCode;
    this.store
      .select(balanceAndRehydrate)
      .pipe(
        filter(
          (dataStore) =>
            !dataStore.updateForm ||
            (dataStore.updateForm && dataStore.updateForm.id === _safe(() => formGroup.get(SpecFieldNames.ID).value)),
        ),
        filter(
          (data) =>
            !!data.rehydrate ||
            ([
              ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK,
              ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK,
              ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK,
            ].includes(data.experienceCode as ORDER_EXPERIENCES) &&
              Object.keys(data.contracts).length !== 0 &&
              data.recipe[0] &&
              !!data.contracts[_safe(() => formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value)]) ||
            (!!value && Object.keys(data.contractsBalance).length !== 0),
        ),
        take(1),
      )
      .subscribe((data) => {
        if (formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value === undefined) {
          this.store.dispatch(new AddedValuesResetActionOP());
        }
        const contractCode = value;
        const contract = _safe(() => data.contractsBalance[formGroup.get(SpecFieldNames.CONTRACT_SELECT).value]);
        const formId = _safe(() => formGroup.get(SpecFieldNames.ID).value);
        const recipeChange = data.recipe.find((recipe) => recipe.id === formId);
        if (
          (recipeChange && recipeChange.values.contractSelect !== value) ||
          !data.updateForm ||
          (data.updateForm && data.updateForm.id === formId && productCode !== prev) ||
          Object.keys(data.addedValues).length === 0
          // valida la primera carga
        ) {
          if (
            !!generateAddedValueFormName(0) &&
            isRehydratation(form.data.id, data.rehydrate, config, generateAddedValueFormName(0))
          ) {
            this.store.dispatch(new RmsSpecificationRemoveRehydrateItem({ id: form.data.id, key: config.name }));
          } else if (
            !!value &&
            !!productCode &&
            data.experienceCode &&
            data.contracts[productCode] &&
            data.contracts[productCode].length &&
            [
              ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK,
              ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK,
              ORDER_EXPERIENCES.ARKIK_UNIVERSAL_LINK,
            ].includes(data.experienceCode as ORDER_EXPERIENCES)
          ) {
            this.store.dispatch(
              new LoadAddedValuesNotFormulatingActionOP({
                productCode: productCode,
                index: 0,
                contractCode: contractCode,
                formId,
              }),
            );
          } else {
            if (!!value && contract && contract.length) {
              this.store.dispatch(
                new LoadAddedValuesNotFormulatingActionOP({
                  productCode: productCode,
                  index: 0,
                  contractCode: value,
                  formId: formId,
                }),
              );
            } else {
              this.store.dispatch(
                new AddedValuesResetActionOPByFormID({
                  formId: formId,
                }),
              );
            }
          }
        }
      });
  }
}

@Injectable()
export class LoadChildAddedValuesNotFormulatingOpenPlatformCommandService implements OnFieldChanged {
  constructor(
    private store: Store<OrderBuilderState>,
    private experiencesService: ExperiencesService,
  ) {}

  private filterRehydrate(form: ConfigurableFormComponent, config: AddedValueFieldConfig) {
    return this.store.select(getRmsRehydrate).pipe(
      take(1),
      filter((rehydrateState) => {
        if (isRehydratation(form.data.id, rehydrateState, config)) {
          this.store.dispatch(new RmsSpecificationRemoveRehydrateItem({ id: form.data.id, key: config.name }));
          return false;
        } else {
          return true;
        }
      }),
    );
  }

  private temperatureVapSelected$(formGroup: UntypedFormGroup): Observable<ValueAddedOP> {
    const summerSet = formGroup.get(SpecFieldNames.SUMMER_SET).value;
    if (!!summerSet) {
      const formId = formGroup.get(SpecFieldNames.ID).value;
      return this.store.select(getTemperatureVaps).pipe(
        take(1),
        map((temperaturesVaps) => {
          if (!!temperaturesVaps && !!temperaturesVaps[formId] && temperaturesVaps[formId].length > 0) {
            const temperaturesAvailable = temperaturesVaps[formId];
            const temperature = temperaturesAvailable.find((addedValue) => addedValue.valueAddedCode === summerSet);
            return temperature;
          } else {
            return null;
          }
        }),
      );
    } else {
      return of(null);
    }
  }

  private accAddedValues(contractCode: string, formGroup: UntypedFormGroup, config: AddedValueFieldConfig) {
    const keyName = `${contractCode}_${formGroup.get(SpecFieldNames.ID).value}`;
    const isTemperatureEnabled$ = this.experiencesService.isTemperatureFeatureEnable$(TemperatureFeatures.NITRO).pipe(
      switchMap((isEnabled) => {
        if (isEnabled) {
          return this.temperatureVapSelected$(formGroup);
        } else {
          return of(null);
        }
      }),
    );
    const tuples$ = this.store.pipe(
      select(getOpenPlatformProductAddedValues(keyName)),
      filter((tuples) => {
        return (
          (tuples && !!tuples.length && tuples[config.index] && tuples[config.index].addedValues !== undefined) ||
          config.index === -1
        );
      }),
      take(1),
    );

    const combineTuples$ = combineLatest([tuples$, isTemperatureEnabled$]).pipe(
      map(([tuples, temperature]) => {
        // Accumulate added values
        const rangeTuples = range(config.index + 1).map((_, index) => {
          const family = formGroup.get(generateAddedValueFormName(index)).value;
          if (tuples[index] && tuples[index].addedValues) {
            return tuples[index].addedValues.find((adddedValue) => adddedValue.valueAddedCode === family);
          } else {
            return null;
          }
        });
        if (temperature) {
          rangeTuples.push(temperature);
        }
        return rangeTuples;
      }),
      filter((addedValue) => !!addedValue && !!addedValue.length),
    );
    return combineTuples$;
  }

  onFieldChanged(
    value: number,
    formGroup: UntypedFormGroup,
    form: ConfigurableFormComponent,
    config: AddedValueFieldConfig,
  ): void {
    this.filterRehydrate(form, config).subscribe(() => {
      // Guard
      const productCode = formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value;
      const contractCode = formGroup.get(SpecFieldNames.CONTRACT_SELECT).value;
      const formId = formGroup.get(SpecFieldNames.ID).value;

      if (config.limit || !productCode) {
        return;
      }

      // Case when unselecting first added value
      if (!value && config.index === 0 && !!productCode && !!contractCode) {
        this.store.dispatch(
          new LoadAddedValuesNotFormulatingActionOP({
            productCode: productCode,
            index: 0,
            contractCode: contractCode,
            formId,
          }),
        );
        return;
      }

      const valueAddedControl = formGroup.get(generateAddedValueFormName(0));
      const temperatureUnderDisable = formGroup.get(SpecFieldNames.TEMPERATURE_UNDER_DISABLED);
      if (
        !value &&
        config.index === -1 &&
        !!productCode &&
        !!contractCode &&
        valueAddedControl.dirty &&
        valueAddedControl &&
        valueAddedControl.value &&
        !temperatureUnderDisable
      ) {
        valueAddedControl.setValue(undefined, { emitEvent: true });
        valueAddedControl.setErrors(null);
        valueAddedControl.markAsPristine();
        valueAddedControl.markAsUntouched();
        valueAddedControl.updateValueAndValidity();
        return;
      }

      // Get entire selected object
      this.accAddedValues(contractCode, formGroup, config).subscribe((addedValues) => {
        if (productCode) {
          // Unselect next one to avoid auto-selection when fetching finishes
          const control = formGroup.get(generateAddedValueFormName(config.index + 1));
          const userFormControl =
            config.index === -1
              ? formGroup.get(SpecFieldNames.SUMMER_SET)
              : formGroup.get(generateAddedValueFormName(config.index));
          if (userFormControl.dirty && control && control.value) {
            control.setValue(undefined);
          } else if (userFormControl.pristine && userFormControl.value) {
            control.updateValueAndValidity();
          }
          const contractCodeValue = formGroup.get(SpecFieldNames.CONTRACT_SELECT).value;
          const formIdValue = formGroup.get(SpecFieldNames.ID).value;
          if (!!contractCodeValue) {
            this.store.dispatch(
              new LoadAddedValuesNotFormulatingActionOP({
                productCode: productCode,
                index: config.index + 1,
                addedValues: addedValues,
                contractCode: contractCodeValue,
                formId: formIdValue,
              }),
            );
          }
        }
      });
    });
  }
}
